import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader } from '@mui/material';
import { Box, Typography, Chip,Button,Grid } from '@mui/material';
import AuthHeader from '../../../Auth';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { format, isWithinInterval } from 'date-fns';
import HotelIcon from "@mui/icons-material/Hotel";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import BalconyIcon from "@mui/icons-material/Balcony";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";



// Custom styled components remain the same as before...
const StyledCard = styled(Card)({
  maxWidth: '1200px',
  margin: '2rem auto',
  padding: '2rem',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
});

const DetailGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '2rem',
  marginBottom: '2rem',
});

const DetailItem = styled('div')({
  '& .label': {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  '& .value': {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#333',
  },
});

const CalendarSection = styled('div')({
  marginTop: '2rem',
  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .booked-date': {
    backgroundColor: '#4CAF50 !important',
    color: 'white !important',
  },
  '& .booked-date:hover': {
    backgroundColor: '#45a049 !important',
  },
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: '#4CAF50 !important',
  }
});
const CalendarContainer = styled('div')({
  display: 'flex',
  gap: '20px', 
  alignItems: 'center',
});


const DurationBadge = styled('div')({
  display: 'inline-block',
  padding: '0.5rem 1rem',
  backgroundColor: '#4CAF50',
  color: 'white',
  borderRadius: '4px',
  marginTop: '1rem',
  fontSize: '0.875rem',
});

const BedDetails = () => {

  const [bedData, setBedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchBedDetails = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const payload = {
          BookFromDt: params.get('fromDate'),
          BookToDt: params.get('toDate'),
          PropId: parseInt(params.get('propId')),
          Flag: "Data",
          RoomBedId: parseInt(params.get('bedId'))
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}RoomBedMst/GetdrpRoomBedMst`,
          payload,
          AuthHeader()
        );

        if (response.data.Status === 0 && response.data.Data.length > 0) {
          setBedData(response.data.Data[0]);
        }
      } catch (error) {
        console.error("Error fetching bed details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBedDetails();
  }, [location]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!bedData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-lg text-gray-600"></p>
      </div>
    );
  }

  const fromDate = new Date(bedData.BookFromDt);
  const toDate = new Date(bedData.BookToDt);
  const durationInDays = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));

  const shouldDisableDate = (date) => {
    return !isWithinInterval(date, { start: fromDate, end: toDate });
  };

  const handleBookNow = () => {
    if (bedData) {
      // Save bed data to localStorage
      localStorage.setItem("selectedBedData", JSON.stringify(bedData));
  
      // Notify parent to fetch updated data
      if (window.opener) {
        window.opener.postMessage({ action: "fetchBedData" }, "*");
      }
  
      // Close the current tab
      window.close();
    } else {
      console.error("No bed data available to book.");
    }
  };
  

  return (
    <StyledCard>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ 
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: '#1a1a1a',
          mb: 2 
        }}>
          Bed Details
        </Typography>
      
          <Typography variant="h5" sx={{ mb: 2,fontSize: '1.2rem', fontWeight: "bold" }}>
          {bedData.PropName}
        </Typography>
        <Chip
          label={`Duration: ${Math.ceil(
            (new Date(bedData.BookToDt) - new Date(bedData.BookFromDt)) /
              (1000 * 60 * 60 * 24)
          )} days`}
          color="success"
          sx={{ mb: 2 }}
        />
      </Box>
      
          <h2 className="text-xl font-semibold mb-4" style={{ fontSize: '1.2rem', }}>Booking Duration</h2>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
         
          <CalendarContainer>
              <DatePicker
                label="From Date"
                value={fromDate}
                readOnly={false}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    sx: { backgroundColor: '#f0f9f0',
                      width: '250px' }
                  },
                  day: {
                    sx: {
                      '&:not(.Mui-disabled)': {
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#45a049'
                        }
                      }
                    }
                  }
                }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                readOnly={false}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    sx: { backgroundColor: '#f0f9f0',
                      width: '250px', gap: '50px' }
                  },
                  day: {
                    sx: {
                      '&:not(.Mui-disabled)': {
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#45a049'
                        }
                      }
                    }
                  }
                }}
              />
           </CalendarContainer>
          </LocalizationProvider>
        
      
     

<CardContent>
      

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <HotelIcon sx={{ mr: 1 }} /> Bed Number: {bedData.RoombedName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <MeetingRoomIcon sx={{ mr: 1 }} /> Room Number: {bedData.RoomNo}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <LocationCityIcon sx={{ mr: 1 }} /> Floor: {bedData.FloorName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <BalconyIcon sx={{ mr: 1 }} /> View: {bedData.RoomViewNAme}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <SquareFootIcon sx={{ mr: 1 }} /> Area: {bedData.SqFt} sq.ft
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography display="flex" alignItems="center">
              <AttachMoneyIcon sx={{ mr: 1 }} /> Rent: ₹{bedData.BedRent}
            </Typography>
          </Grid>
        </Grid>

        <Box mt={3}>
        <button
  style={{
    height: '100%',
    backgroundColor: '#635bff',
    color: 'white',
    padding: '9px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  }}
  onClick={handleBookNow}
>
  Book Now
</button>

        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default BedDetails;